import { POST, GET } from "./utils";
import type {
    ModelMetrics,
    VersionMetricsResponse,
    TestSource,
    TestSourcesResponse
} from "@/components/flow/deploy/Steps/Model/types";

/**
 * Mark a model change as seen by the current user.
 * Called when the user closes the model modal.
 */
export async function markModelChangeAsSeen(
    rapidDataId: string,
    changeId: string
): Promise<{ success: boolean }> {
    return POST(`/api/rapiddata/${rapidDataId}/model-changes/${changeId}/seen`, {});
}

/**
 * Fetch model metrics (mAP, precision, recall) for a specific version.
 * The versionRef format is "dataset-url/version-number" (e.g., "my-dataset/1")
 */
export async function getVersionMetrics(versionRef: string): Promise<VersionMetricsResponse> {
    // versionRef format: "dataset-url/version-number"
    // Use the slash directly as a path separator — do NOT encodeURIComponent the whole ref,
    // because %2F is rejected by some proxies (Fastly, Firebase Hosting, Cloud Run).
    return GET(`/api/version/${versionRef}/metrics`);
}

/**
 * Parse model training results into standardized ModelMetrics format.
 * Handles different project types (detection, classification, segmentation).
 */
export function parseTrainResults(
    trainResults: Record<string, number> | null
): ModelMetrics | null {
    if (!trainResults) return null;

    const metrics: ModelMetrics = {};

    // Detection models (most common for Rapid)
    if (trainResults.map !== undefined) {
        metrics.map = parseFloat((trainResults.map * 100).toFixed(2));
    }
    if (trainResults.precision !== undefined) {
        metrics.precision = parseFloat((trainResults.precision * 100).toFixed(2));
    }
    if (trainResults.recall !== undefined) {
        metrics.recall = parseFloat((trainResults.recall * 100).toFixed(2));
    }

    // Classification models
    if (trainResults.accuracy !== undefined) {
        metrics.accuracy = parseFloat((trainResults.accuracy * 100).toFixed(2));
    }

    // Semantic segmentation
    if (trainResults.val_mean_iou !== undefined) {
        metrics.miou = parseFloat((trainResults.val_mean_iou * 100).toFixed(2));
    }

    // Return null if no metrics were found
    return Object.keys(metrics).length > 0 ? metrics : null;
}

/**
 * Fetch test split sources for model comparison.
 * This returns just the sources - inference is run separately via /api/infer.
 *
 * @param rapidDataId - The rapid data ID to fetch sources for
 * @param limit - Maximum number of sources to fetch (default 10, max 20)
 */
export async function getTestSources(
    rapidDataId: string,
    limit: number = 10
): Promise<TestSourcesResponse> {
    const params = new URLSearchParams({ rapidDataId, limit: String(limit) });
    return GET(`/api/dataset/test-sources?${params.toString()}`);
}
